import { useMemo, useState } from 'react'
import { AxiosError } from 'axios'
import { Box, Flex, Spinner, Text } from '@radix-ui/themes'
import { IconPencil, IconTrash } from '@tabler/icons-react'
import clsx from 'clsx'
import { Loader } from 'lucide-react'
import { toast } from 'sonner'
import { FormatFullDateMonthYear } from '@/utils/time'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { useDeleteQuestion } from '../hooks/useDeleteQuestion'
import QuestionTypeItem from './QuestionTypeItem'

interface QuestionHeaderProps {
  isEditing?: boolean
  question_id: number
  createdAt: string | number
  questionType: string
  isCaseStudy?: boolean
  saveDisabled?: boolean
  cantEdit?: boolean
  isInDetail?: boolean
  isUpdating?: boolean
  questionInfo: string
  section: string
  in_pt?: boolean
  setIsEditing?: (value: boolean) => void
  handleSave?: () => void
  handleCancel?: () => void
  onDeleteSuccess?: () => void
}

const QuestionHeader = ({
  isUpdating,
  questionType,
  isCaseStudy = false,
  createdAt,
  isEditing,
  saveDisabled,
  cantEdit,
  questionInfo,
  question_id,
  section = '',
  in_pt = false,
  setIsEditing,
  handleSave,
  handleCancel,
  onDeleteSuccess,
}: QuestionHeaderProps) => {
  const { deleteQuestionAsync, isDeletingQuestion } = useDeleteQuestion()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const isCreateTest = useMemo(() => {
    return window.location.href.includes('create-')
  }, [])

  const handleConfirmDelete = async () => {
    try {
      await deleteQuestionAsync(question_id)
      toast.success('Question deleted!')
      setDeleteDialogOpen(false)
      onDeleteSuccess?.()
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        const errorMsg =
          (error.response?.data as { detail?: string })?.detail ||
          'Failed to delete question'
        toast.error(errorMsg)
      }
    }
  }

  return (
    <>
      <Flex
        justify={'between'}
        align={'center'}
        className='w-full font-light text-[#475467]'
      >
        <p className='font-light text-[#475467] select-text'>
          {questionInfo ?? ''}
        </p>
        <Flex gap={'12px'} align={'center'} className='text-sm'>
          {!isCreateTest && (
            <>
              {cantEdit && (
                <>
                  {isEditing ? (
                    <div className='flex max-h-9 items-center gap-2'>
                      <Button
                        className='h-9'
                        variant={'outline'}
                        onClick={
                          handleCancel ||
                          (() => setIsEditing && setIsEditing(false))
                        }
                      >
                        Cancel
                      </Button>
                      <Button
                        className='h-9 min-w-[62px]'
                        onClick={handleSave}
                        disabled={saveDisabled}
                      >
                        {isUpdating ? <Spinner /> : 'Save'}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Button
                        onClick={() => setIsEditing && setIsEditing(true)}
                      >
                        <IconPencil /> Edit
                      </Button>
                      <div
                        className='inline-flex shrink-0'
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button
                          className='h-9 min-w-[98px] bg-red-400 hover:bg-red-500'
                          disabled={in_pt || isDeletingQuestion}
                          onClick={() => setDeleteDialogOpen(true)}
                        >
                          <IconTrash /> Delete
                        </Button>
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          )}
          <p className='font-light'>
            Date created: {FormatFullDateMonthYear(createdAt)}
          </p>

          <Box
            className={clsx(
              'rounded-[8px] border border-[#D5D7DA] bg-white px-[10px] py-[4px]'
            )}
          >
            <Text className='text-sm text-[#475467]'>Section {section}</Text>
          </Box>

          {!!questionType && <QuestionTypeItem title={questionType} />}
        </Flex>
        {isCaseStudy && (
          <Box
            className={clsx(
              'rounded-[8px] border border-[#D5D7DA] bg-white px-[10px] py-[4px]'
            )}
          >
            <Text className='text-sm text-[#475467]'>Case Study</Text>
          </Box>
        )}
      </Flex>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Delete question</AlertDialogTitle>
            <AlertDialogDescription>
              Deleting this question will remove it from all chapters.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isDeletingQuestion}
              className='min-w-[85px] bg-red-600 hover:bg-red-700'
              onClick={(e) => {
                e.stopPropagation()
                handleConfirmDelete()
              }}
            >
              {isDeletingQuestion ? (
                <Loader className='animate-spin' />
              ) : (
                <>Confirm</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export default QuestionHeader
